import type { LangCode } from "@/lib/types";

export type Dict = Record<string, string>;

/**
 * Access to the active locale's translation dictionary.
 *
 * The nine locale JSON files total ~1.3 MB. They used to be statically
 * imported by I18nContext and store-i18n, which put every one of them into the
 * shared client chunk — so a Turkish visitor downloaded the Arabic, Russian,
 * Japanese, Korean, Chinese, German, Spanish and English dictionaries as well.
 *
 * Instead the server resolves ONE dictionary per request, already merged over
 * English so lookups need no fallback chain, and inlines it into the HTML.
 * The client reads that. No locale JSON reaches the browser bundle at all.
 *
 * The static imports live in src/server.ts, which is the nitro entry and is
 * never part of the client build — that is what keeps them out.
 */
export const I18N_GLOBAL_KEY = "__VARS_I18N__";

interface I18nPayload {
  lang: LangCode;
  dict: Dict;
  /** Content-hashed URL of this locale's complete dictionary, if sliced. */
  rest?: string;
}

type ServerStore = {
  lang?: string;
  path?: string;
  i18n?: I18nPayload;
  /** Keys this request actually looked up. See `recordingDict`. */
  seen?: Set<string>;
  /** Cached proxy so one request does not allocate a new one per lookup. */
  recorder?: Dict;
  /** Content-hashed URL of this locale's complete dictionary. */
  restUrl?: string;
};

function serverStore(): ServerStore | undefined {
  const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
    { getStore: () => ServerStore | undefined } | undefined;
  return storage?.getStore();
}

function serverPayload(): I18nPayload | undefined {
  return serverStore()?.i18n;
}

function clientPayload(): I18nPayload | undefined {
  return (globalThis as unknown as Record<string, I18nPayload | undefined>)[I18N_GLOBAL_KEY];
}

/**
 * The dictionary, wrapped so every lookup is recorded.
 *
 * This is how the inlined payload gets sliced without a hand-maintained map of
 * route → namespace. Such a map has to be complete or pages render raw keys
 * server-side, and keeping one accurate across 46 routes and every shared
 * component is precisely the kind of bookkeeping that rots. Recording what the
 * render actually asked for is exact by construction instead.
 *
 * Only `get` is trapped, which is safe because every consumer does a keyed
 * lookup — `dict[key]`, directly or via `t()`. Nothing enumerates the
 * dictionary (checked across all 22 `activeDict()` call sites). If something
 * ever does, `ownKeys` would need to record everything, and the slice for that
 * page would correctly collapse back to the full dictionary.
 */
function recordingDict(store: ServerStore, dict: Dict): Dict {
  if (store.recorder) return store.recorder;
  const seen = (store.seen ??= new Set<string>());
  const proxy = new Proxy(dict, {
    get(target, prop) {
      if (typeof prop === "string") seen.add(prop);
      return target[prop as keyof typeof target];
    },
  });
  store.recorder = proxy;
  return proxy;
}

/**
 * The active dictionary. Empty only if neither side supplied one, in which
 * case callers fall back to the key or to a supplied default — the same
 * degradation as a missing key, never a crash.
 */
export function activeDict(): Dict {
  const store = serverStore();
  if (store?.i18n) return recordingDict(store, store.i18n.dict);
  return clientPayload()?.dict ?? {};
}

/** Locale the active dictionary belongs to. */
export function activeDictLang(): LangCode | undefined {
  return (serverPayload() ?? clientPayload())?.lang;
}

let fullDictPromise: Promise<boolean> | undefined;

/**
 * Fetch the rest of this locale's dictionary and merge it into the inlined
 * slice. Resolves to whether anything actually changed.
 *
 * The document carries only what the page rendered plus shared chrome, so a
 * string that appears on interaction — or on a client-side navigation to
 * another route — may not be present yet. This closes that window. The URL is
 * content-addressed and served `immutable`, so it is one edge-cached request
 * per locale per deploy, and it costs nothing on a repeat visit.
 *
 * Every failure mode degrades to "keep the slice we already have". A missing
 * key renders as the key, which is the same behaviour this dictionary has
 * always had for an untranslated string, so a failed fetch is a smaller
 * problem than a blocked render would be.
 */
export function hydrateFullDict(): Promise<boolean> {
  if (fullDictPromise) return fullDictPromise;

  const payload = clientPayload();
  const url = payload?.rest;
  if (!payload || !url) return (fullDictPromise = Promise.resolve(false));

  fullDictPromise = fetch(url, { credentials: "omit" })
    .then((r) => (r.ok ? (r.json() as Promise<Dict>) : undefined))
    .then((full) => {
      if (!full) return false;
      // The inlined slice wins on conflict. It came from the same build and is
      // already correct; assigning it second makes that explicit rather than
      // relying on the two being identical.
      payload.dict = { ...full, ...payload.dict };
      return true;
    })
    .catch(() => false);

  return fullDictPromise;
}

let firstLoadSeen = false;

/**
 * Hold a client-side navigation until the full dictionary is in hand — but
 * never the initial load.
 *
 * The distinction is the whole point, and the first version missed it.
 * Awaiting unconditionally in the root's `beforeLoad` means the router cannot
 * finish its very first load until a network round trip completes, which
 * delays interactivity on every cold visit and cost five e2e failures. It also
 * buys nothing there: the slice inlined in the document is complete for the
 * page that was just rendered.
 *
 * A later navigation is different. That route's namespaces may not be inlined
 * — `/account/profile` reached from `/account` is exactly this — and without
 * the wait its labels render as raw keys. By then the fetch has been running
 * since hydration, so this is normally an already-settled promise.
 *
 * The wait is BOUNDED, and that matters more than it looks. Waiting on the
 * promise itself makes every first navigation hostage to a network fetch: on a
 * fast connection it has long since resolved, but on a slow one the click does
 * nothing until it lands. `filters.spec` caught exactly that — a chip click on
 * /faqs failing to change the URL within five seconds, on a page whose full
 * dictionary is 4,066 keys.
 *
 * So the navigation waits at most DICT_WAIT_MS and then proceeds regardless.
 * The two failure modes are not equal: a stalled click looks broken and the
 * visitor clicks again, while a key that renders as its own name for a moment
 * is the behaviour this dictionary has always had for an untranslated string,
 * and it corrects itself as soon as the fetch lands.
 */
const DICT_WAIT_MS = 400;

export function whenDictReady(): Promise<unknown> {
  if (typeof window === "undefined") return Promise.resolve();
  if (!firstLoadSeen) {
    firstLoadSeen = true;
    void hydrateFullDict(); // start it, but do not block the first load
    return Promise.resolve();
  }
  return Promise.race([
    hydrateFullDict(),
    new Promise((resolve) => setTimeout(resolve, DICT_WAIT_MS)),
  ]);
}

/** Serialized <script> body that hands the dictionary to the client. */
export function i18nBootstrapScript(payload: I18nPayload): string {
  // JSON.stringify twice: the inner call serializes the payload, the outer one
  // produces a JS string literal that cannot terminate the surrounding
  // <script> element or inject markup.
  return `window[${JSON.stringify(I18N_GLOBAL_KEY)}]=JSON.parse(${JSON.stringify(
    JSON.stringify(payload),
  )});`;
}

/**
 * Server-side payload for the bootstrap script, or undefined on the client.
 *
 * head() runs isomorphically. Emitting the script only when a server payload
 * exists means the dictionary is serialized exactly once, during SSR.
 */
export function serverI18nPayload(): I18nPayload | undefined {
  return serverPayload();
}

/**
 * Namespaces inlined on every page regardless of what the render touched.
 *
 * Recording catches everything rendered server-side, but not strings that only
 * appear after an interaction — a dropdown, the consent banner's second state,
 * a form's validation message, the 404 view. Those would otherwise flash their
 * raw key in the window between hydration and the remainder arriving.
 *
 * The whole set is ~9.8 KB gzip against the 90.4 KB it replaces, so the
 * trade is heavily worth making. Keep it to chrome: anything page-specific
 * belongs to the recording, not here.
 */
const ALWAYS_INLINE = new Set([
  "nav",
  "footer",
  "common",
  "cta",
  "error",
  "notFound",
  "consent",
  "whatsapp",
  "widget",
  "search",
  "form",
  "faqSection",
  "markdown",
  "turnstile",
  "cart",
  "productCard",
  "relatedProducts",
  "categoryPage",
  "catalog",
  "countdown",
  "auth",
  "quoteForm",
]);

function namespaceOf(key: string): string {
  const dot = key.indexOf(".");
  return dot === -1 ? key : key.slice(0, dot);
}

function isAlwaysInlined(key: string): boolean {
  if (ALWAYS_INLINE.has(namespaceOf(key))) return true;
  // The header mega-menu renders translated product titles on every page
  // (SiteHeader.tsx:725). Without these, every non-English locale would show
  // English product names in the nav until the remainder loaded. 119 keys,
  // ~2.3 KB gzip.
  return key.startsWith("product.") && key.endsWith(".title");
}

/**
 * The payload to inline, given what this request's render actually used.
 *
 * Returns `undefined` on the client, where the SSR-emitted script has already
 * populated `window`.
 */
export function serverSlicedPayload(): (I18nPayload & { rest?: string }) | undefined {
  const store = serverStore();
  const payload = store?.i18n;
  if (!payload) return undefined;

  const full = payload.dict;

  // Expand the recorded keys to their NAMESPACES, and take the whole of each.
  //
  // Slicing to the exact keys the render touched was the first attempt, and it
  // was too sharp. A string that only appears after an interaction is never
  // touched during SSR, so it was not in the slice — and it rendered as its own
  // key. Two e2e tests caught it: the FAQ hub's "No question matches" empty
  // state (only reached by typing a query that matches nothing) and the change
  // password button on /account/profile.
  //
  // Namespaces are the right unit because that is how this dictionary is
  // organised: strings that appear together on a page share a prefix. If a page
  // rendered any `faq.*` key, it may well need the rest of `faq.*` before the
  // visitor leaves, and taking the namespace whole costs a few KB while
  // removing the race entirely.
  // `product` is the one exception, and it has to be. It holds 1,249 of the
  // 3,766 keys — 123 KB, a third of the dictionary — and the header mega-menu
  // looks up a product title on EVERY page. Expanding that one lookup to the
  // whole namespace would inline the entire catalogue everywhere and undo the
  // change. Product strings stay key-level: a product detail page renders its
  // own product's fields during SSR, so recording already covers them, and the
  // titles the nav needs are in ALWAYS_INLINE.
  const seen = store?.seen ?? new Set<string>();
  const namespaces = new Set<string>();
  for (const key of seen) {
    const ns = namespaceOf(key);
    if (ns !== "product") namespaces.add(ns);
  }

  const slice: Dict = {};
  for (const key of Object.keys(full)) {
    if (isAlwaysInlined(key) || namespaces.has(namespaceOf(key)) || seen.has(key)) {
      slice[key] = full[key];
    }
  }

  // `restUrl` is supplied by src/server.ts, which owns the dictionaries and
  // hashes their content. Deriving it from a build-time constant here was the
  // first attempt and was wrong: the constant would not change between deploys,
  // so an immutable-cached dictionary would outlive the copy it belongs to and
  // a locale fix could sit invisible behind the edge cache for a year.
  return { lang: payload.lang, dict: slice, rest: store?.restUrl };
}
